import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail, Phone, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import StarMotif from "../components/StarMotif";
import InfoTooltip from "../components/admin/InfoTooltip";
import { trackEvent, EVENTS } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "AI Readiness and Adoption",
  "Digital Transformation",
  "Project / PMO Improvement",
  "Business Change and Adoption",
  "Supplier Optimisation",
  "ITSM Operational Excellence",
  "Data and Insight",
  "Benefits Realisation",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organisation: "",
    role: "",
    area_of_interest: INTERESTS[0],
    message: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      trackEvent(EVENTS.DISCOVERY_ENQUIRY);
      toast.success("Thank you — we'll be in touch within one working day.");
      setForm({
        full_name: "",
        email: "",
        organisation: "",
        role: "",
        area_of_interest: INTERESTS[0],
        message: "",
        consent: false,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again or email us directly.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Contact</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 max-w-4xl" data-testid="contact-headline">
            Book a free 30-minute discovery.
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            Bring one challenge. We'll spend 30 focused minutes on it and you'll leave with <strong className="text-white">three prioritised next steps</strong> — written up afterwards in a one-pager you can share with your team. No slide pitch. No obligation.
          </p>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-8 text-[13px] text-zinc-400">
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#A179FA]" />30 minutes</span>
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#A179FA]" />By video or phone</span>
            <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#A179FA]" />One-pager summary sent within 24 hours</span>
          </div>
        </div>
      </section>

      <section className="bg-white ek-section">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* CONTACT INFO */}
            <div className="lg:col-span-4">
              <div className="ek-eyebrow">Get in touch</div>
              <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black">
                Direct contact.
              </h2>
              <p className="ek-body mt-6">
                Prefer to start with a call or email? Reach us directly using the details below — we respond within one working day.
              </p>

              <ul className="mt-10 space-y-6 text-[15px]">
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6] shrink-0">
                    <Mail size={18} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="ek-label">Email</div>
                    <a href="mailto:info@ekselens.com" className="text-black hover:text-[#7737F6]">info@ekselens.com</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6] shrink-0">
                    <Phone size={18} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="ek-label">Phone</div>
                    <a href="tel:+442078747217" className="text-black hover:text-[#7737F6]">+44 (0) 20 7874 7217</a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6] shrink-0">
                    <MapPin size={18} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="ek-label">Address</div>
                    <p className="text-black font-light leading-relaxed">
                      5th Floor, 167-169 Great Portland Street,<br />London, W1W 5PF
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            {/* FORM */}
            <div className="lg:col-span-8">
              {submitted ? (
                <div className="ek-card-light p-10 lg:p-14 text-center" data-testid="contact-success">
                  <div className="w-14 h-14 mx-auto bg-[#7737F6] text-white flex items-center justify-center mb-6">
                    <Check size={28} strokeWidth={2} />
                  </div>
                  <h3 className="ek-heading text-3xl text-black">Thank you.</h3>
                  <p className="ek-body mt-4 max-w-md mx-auto">
                    Your enquiry has been received. A member of the Ekselens team will be in touch within one working day to arrange your discovery conversation.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="ek-btn ek-btn-outline-dark mt-8"
                    data-testid="contact-submit-another"
                  >
                    Submit another enquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  className="border border-zinc-200 p-8 md:p-10 bg-white"
                  data-testid="contact-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="full_name" className="ek-label">Full name</label>
                      <input
                        id="full_name"
                        name="full_name"
                        type="text"
                        required
                        value={form.full_name}
                        onChange={onChange}
                        className="ek-input"
                        data-testid="contact-input-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="ek-label">Email address</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={onChange}
                        className="ek-input"
                        data-testid="contact-input-email"
                      />
                    </div>
                    <div>
                      <label htmlFor="organisation" className="ek-label">Organisation</label>
                      <input
                        id="organisation"
                        name="organisation"
                        type="text"
                        required
                        value={form.organisation}
                        onChange={onChange}
                        className="ek-input"
                        data-testid="contact-input-organisation"
                      />
                    </div>
                    <div>
                      <label htmlFor="role" className="ek-label">
                        Role
                        <InfoTooltip testid="contact-role-info">
                          Your job title or function (e.g. CIO, Transformation Director). It helps us tailor the conversation to your seniority and remit.
                        </InfoTooltip>
                      </label>
                      <input
                        id="role"
                        name="role"
                        type="text"
                        required
                        value={form.role}
                        onChange={onChange}
                        className="ek-input"
                        data-testid="contact-input-role"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="area_of_interest" className="ek-label">
                        Area of interest
                        <InfoTooltip testid="contact-interest-info">
                          The capability you'd most like to discuss. Pick the closest match — we'll confirm scope in the discovery call. Choose "Other" if nothing fits.
                        </InfoTooltip>
                      </label>
                      <select
                        id="area_of_interest"
                        name="area_of_interest"
                        value={form.area_of_interest}
                        onChange={onChange}
                        className="ek-select"
                        data-testid="contact-input-interest"
                      >
                        {INTERESTS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="message" className="ek-label">
                        Message
                        <InfoTooltip testid="contact-message-info">
                          A few lines on your context, the challenge, and the outcome you're after. The more specific you are, the more focused your 30-minute discovery will be.
                        </InfoTooltip>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={onChange}
                        className="ek-textarea"
                        placeholder="Briefly describe your context, challenge or desired outcome."
                        data-testid="contact-input-message"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={form.consent}
                          onChange={onChange}
                          required
                          className="mt-1 w-4 h-4 accent-[#7737F6]"
                          data-testid="contact-input-consent"
                        />
                        <span className="text-[14px] text-zinc-700 font-light leading-relaxed">
                          I consent to Ekselens Consulting contacting me about my enquiry. We will only use your details to respond to this enquiry — see our privacy policy.
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-zinc-500 text-xs">We respond within one working day.</p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="ek-btn ek-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="contact-submit-button"
                    >
                      {submitting ? "Sending…" : (
                        <>
                          Send enquiry <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
